import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

interface SuccessSparklesProps {
  show: boolean;
  onComplete?: () => void;
}

const SuccessSparkles = ({ show, onComplete }: SuccessSparklesProps) => {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    angle: (i * 30) * (Math.PI / 180),
    delay: i * 0.05,
  }));

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {show && (
        <motion.div
          className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Central burst */}
          <motion.div
            className="absolute"
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: [0, 1.5, 2], opacity: [1, 0.8, 0] }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="w-32 h-32 rounded-full bg-gradient-to-r from-emerald-400/30 to-yellow-400/30 blur-xl" />
          </motion.div>

          {/* Sparkle particles */}
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute"
              initial={{ 
                x: 0, 
                y: 0, 
                scale: 0, 
                opacity: 1,
                rotate: 0
              }}
              animate={{ 
                x: Math.cos(particle.angle) * 150,
                y: Math.sin(particle.angle) * 150,
                scale: [0, 1.2, 0],
                opacity: [1, 1, 0],
                rotate: 360
              }}
              transition={{ 
                duration: 0.8, 
                delay: particle.delay,
                ease: "easeOut"
              }}
            >
              <Sparkles 
                className="w-5 h-5" 
                style={{ 
                  color: particle.id % 3 === 0 
                    ? '#10b981' 
                    : particle.id % 3 === 1 
                      ? '#fbbf24' 
                      : '#D4A574' 
                }}
              />
            </motion.div>
          ))}

          {/* Success checkmark */}
          <motion.div
            className="absolute bg-emerald-500 rounded-full p-4 shadow-lg shadow-emerald-500/50"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: [0, 1.2, 1], rotate: 0 }}
            transition={{ 
              duration: 0.5, 
              delay: 0.2,
              type: "spring",
              stiffness: 200
            }}
          >
            <motion.svg
              className="w-8 h-8 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <motion.path
                d="M5 12l5 5L20 7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.4, delay: 0.4 }}
              />
            </motion.svg>
          </motion.div>

          {/* Confetti dots */}
          {Array.from({ length: 20 }, (_, i) => (
            <motion.div
              key={`confetti-${i}`}
              className="absolute w-2 h-2 rounded-full"
              style={{
                backgroundColor: ['#10b981', '#fbbf24', '#D4A574', '#ec4899', '#8b5cf6'][i % 5]
              }}
              initial={{ 
                x: 0, 
                y: 0,
                scale: 0
              }}
              animate={{ 
                x: (Math.random() - 0.5) * 300,
                y: (Math.random() - 0.5) * 300 + 100,
                scale: [0, 1, 0],
                rotate: Math.random() * 720
              }}
              transition={{ 
                duration: 1,
                delay: 0.1 + Math.random() * 0.3,
                ease: "easeOut"
              }}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SuccessSparkles;
